// Copyright (c) 2019-present Ithpower, Inc. All Rights Reserved.
// See LICENSE.txt for license information.
import React, {ReactNode} from 'react';
import {message} from 'antd';
import {Location, History} from 'history';

import {loadEnterprisesByUserId} from 'server/actions/general';
import {IAIClient} from 'server/client';
import loadImg from 'images/login/synchronizing.gif';
import errorImg from 'images/login/login_fail.png';
import {IsvType} from 'types/users';
import {CallBackFunc} from 'types/general';
import {urlsafeB64decode} from 'utils/utils';

type State = {
    errorMessage: string,
    status: 'init' | 'error' | 'syncing' | 'invalid',
};

export interface AuthBaseProps {
    location: Location
    history?: History
    clientConfig: any
    setUserInfo?: CallBackFunc
}

export default class TripartiteAuthBase extends React.PureComponent<AuthBaseProps, State> {
    code: string | null | undefined;
    corpId: string | null | undefined;
    isvAppType: IsvType | null | undefined;
    authToken: string | undefined;
    syncTimer: NodeJS.Timeout | undefined;
    jumpLink: string | undefined;

    state: State = {
        errorMessage: '',
        status: 'init',
    };

    handleStatusInit = () => {
        this.setState({
            status: 'init',
        });
    };

    handleSynchronizing = () => {
        this.setState({
            status: 'syncing',
        });
    };

    handleAuthError = () => {
        this.setState({
            status: 'error',
        });
    };

    settingErrorMessage = (message: string) => {
        this.setState({
            errorMessage: message,
        });
    };

    handleAuthInvalid = () => {
        // 用于检测各个平台的唯一标识在URLSearchParams中取不到的异常情况
        this.setState({
            status: 'invalid',
        });
    };

    reTry = () => {
        //
    };

    renderSynchronizing: undefined | (() => ReactNode);

    redirectToHomePage = () => {
        const query = new URLSearchParams(this.props.location.search);
        const redirectTo = decodeURIComponent(query.get('redirect_to') || '');

        // 兼容钉钉会把Url自动decode，前后端协商 将跳转url进行编码
        const redirectUrl = urlsafeB64decode(query.get('safe_b64_redirect_url') || '');
        if (redirectTo || redirectUrl) {
            window.location.replace(redirectTo || redirectUrl);
        } else {
            loadEnterprisesByUserId().then((response) => {
                const {error, data} = response;
                if (error) {
                    message.error('登录失败，请尝试重新登录');
                } else if (data) {
                    const currentEnterpriseId = IAIClient.getEnterpriseId();
                    for (const enterprise of data) {
                        if (enterprise.id === currentEnterpriseId) {
                            let applicationUrl: string | undefined;
                            if (this.isvAppType === 'Performance') {
                                applicationUrl = this.jumpLink || '/perf/';
                            } else if (this.isvAppType === 'Teamwork') {
                                applicationUrl = this.jumpLink || '/iai/';
                            } else if (this.isvAppType === 'Salary') {
                                applicationUrl = this.jumpLink;
                            }
                            if (applicationUrl) {
                                window.location.replace(applicationUrl);
                            } else {
                                message.error('请刷新后重试');
                            }
                            return;
                        }
                    }
                }
            });
        }
    };

    render() {
        let content;
        if (this.state.status === 'syncing' && this.renderSynchronizing) {
            content = this.renderSynchronizing();
        } else if (this.state.status === 'error') {
            let errorText;
            if (this.state.errorMessage) {
                errorText = (
                    <span className='tripartite-app-placeholder-tip'>
                        {this.state.errorMessage}
                    </span>
                );
            } else {
                errorText = (
                    <span className='tripartite-app-placeholder-tip'>
                        {'登录失败'}
                        <span
                            className='tripartite-app-placeholder-link-text'
                            style={{marginLeft: '5px'}}
                            onClick={this.reTry}
                        >{'重试'}</span>
                    </span>
                );
            }
            content = (
                <div className='tripartite-app-placeholder-wrapper'>
                    <img
                        className='content-image'
                        src={errorImg}
                        alt='error'
                    />
                    {errorText}
                </div>
            );
        } else if (this.state.status === 'invalid') {
            content = (
                <div className='tripartite-app-placeholder-wrapper'>
                    <img
                        className='content-image'
                        src={errorImg}
                        alt='invalid'
                    />
                    <span className='tripartite-app-placeholder-tip'>
                        {'登录异常，请关闭应用后重新尝试'}
                    </span>
                </div>
            );
        } else {
            content = (
                <img
                    src={loadImg}
                    className='loading-image'
                    alt='loading'
                />
            );
        }
        return (
            <div
                id='tripartite_application'
                className='tripartite_application'
            >
                {content}
            </div>
        );
    }
}
